import React, { useState } from 'react';

const UtilityComparisonTable = () => {
    const [selectedUtilities, setSelectedUtilities] = useState([]);

    // Placeholder data structure for utility comparison
    const utilityOptions = [
        {
            id: 1,
            name: 'Grid Capacity Analysis',
            type: 'Infrastructure',
            capacity: 'N/A',
            cost: 'N/A',
            efficiency: 'N/A',
            status: 'Coming Soon'
        },
        {
            id: 2,
            name: 'Load Forecasting Model',
            type: 'Analytics',
            capacity: 'N/A',
            cost: 'N/A',
            efficiency: 'N/A',
            status: 'Coming Soon'
        },
        {
            id: 3,
            name: 'Renewable Integration Points',
            type: 'Planning',
            capacity: 'N/A',
            cost: 'N/A',
            efficiency: 'N/A',
            status: 'Coming Soon'
        }
    ];

    const handleSelectUtility = (utilityId) => {
        setSelectedUtilities(prev => 
            prev.includes(utilityId) 
                ? prev.filter(id => id !== utilityId)
                : [...prev, utilityId]
        );
    };

    const handleSelectAll = () => {
        if (selectedUtilities.length === utilityOptions.length) {
            setSelectedUtilities([]);
        } else {
            setSelectedUtilities(utilityOptions.map(utility => utility.id));
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-lg">
            <div className="p-6 border-b border-gray-200">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Utility Planning Comparison</h1>
                <p className="text-gray-600">
                    Compare different utility planning options and infrastructure scenarios.
                </p>
            </div>

            <div className="p-6">
                <div className="mb-4">
                    <button
                        onClick={handleSelectAll}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        {selectedUtilities.length === utilityOptions.length ? 'Deselect All' : 'Select All'}
                    </button>
                    <span className="ml-4 text-sm text-gray-600">
                        {selectedUtilities.length} of {utilityOptions.length} selected
                    </span>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    <input
                                        type="checkbox"
                                        checked={selectedUtilities.length === utilityOptions.length}
                                        onChange={handleSelectAll}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Utility Option
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Capacity
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Cost
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Efficiency
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {utilityOptions.map((utility) => (
                                <tr 
                                    key={utility.id}
                                    className={`hover:bg-gray-50 ${selectedUtilities.includes(utility.id) ? 'bg-blue-50' : ''}`}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <input
                                            type="checkbox"
                                            checked={selectedUtilities.includes(utility.id)}
                                            onChange={() => handleSelectUtility(utility.id)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            {utility.name}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                            {utility.type}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {utility.capacity}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {utility.cost}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {utility.efficiency}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                            {utility.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">
                                Development in Progress
                            </h3>
                            <div className="mt-2 text-sm text-yellow-700">
                                <p>
                                    Utility planning features are currently under development. 
                                    Data and functionality will be added as the module is completed.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UtilityComparisonTable;
